import type { DeployFailureContext } from '@volley/crucible-ipc-types/ipc-channels';
import type { AutoBuildStatus } from '@volley/crucible-ipc-types/build-game-types';
import { isActiveAutoBuild } from './plan-build-handoff';
import type { WorkspaceRunKind } from './run-state';

export type DeployState = 'idle' | 'starting' | 'running' | 'deploying' | 'deployed' | 'error';

export type DeployActionKind = 'idle' | 'blocked' | 'deploying' | 'deployed' | 'failed';

export type DeployActionTone = 'neutral' | 'active' | 'success' | 'warning' | 'error';

export type DeployFailurePrimaryCta = 'fix-in-agent' | 'retry-deploy';
export type DeployFailureSecondaryCta = 'view-github';

export interface DeployActionState {
  kind: DeployActionKind;
  label: string;
  tone: DeployActionTone;
  badgeClassName: string;
  deployButtonLabel: string;
  deployButtonTitle: string;
  deployButtonDisabled: boolean;
  deployButtonGated: boolean;
  failurePrimaryCta?: DeployFailurePrimaryCta;
  failureSecondaryCtas: DeployFailureSecondaryCta[];
  disabledReason?: string;
  debugSource: string;
}

export interface DeployActionInput {
  deployState: DeployState;
  deployFailureContext?: DeployFailureContext;
  localFailedCheck?: string | null;
  ciTriggeredThisDeploy: boolean;
  hasAnthropicKey: boolean;
  /**
   * #3132 — build-readiness verdict from {@link deriveDeployBuildReadiness}.
   * Optional so callers without an interview-status source keep the
   * pre-#3132 always-enabled behaviour (absence === `'ready'`).
   */
  buildReadiness?: DeployBuildReadiness;
  /**
   * #3494 — current checked-out git branch (`GameStatus.localGit.branch`).
   * Feeds the `not-on-deploy-branch` precondition. Absent === not blocking.
   */
  currentBranch?: string | null;
  /**
   * #3495 — true ONLY when HEAD positively has nothing new to deploy.
   * Undefined leaves the button enabled (offline / not-yet-checked).
   */
  upToDate?: boolean;
}

/**
 * #3132 — build-readiness verdict for the Deploy-to-TV button.
 *
 *   - `'ready'`             — deployable (built, built-partial, unknown,
 *                             or no interview state at all).
 *   - `'not-built'`         — the game has never been built (interview /
 *                             plan-review still open).
 *   - `'build-in-progress'` — Phase B is dispatched or actively building.
 */
export type DeployBuildReadiness = 'ready' | 'not-built' | 'build-in-progress';

export const DEPLOY_NOT_BUILT_REASON =
  "This game hasn't been built yet. Finish the build before deploying to TV.";

export const DEPLOY_BUILD_IN_PROGRESS_REASON =
  'A build is in progress. Wait for it to finish before deploying to TV.';

const LEGACY_DEPLOY_HANDOFF_FRESH_MS = 15 * 60 * 1000;
const LEGACY_DEPLOY_HANDOFF_FUTURE_SKEW_MS = 60 * 1000;

export function isFreshLegacyDeployHandoff(
  updatedAt: string | null | undefined,
  nowMs: number,
): boolean {
  if (!updatedAt) return false;
  const updatedAtMs = Date.parse(updatedAt);
  const ageMs = nowMs - updatedAtMs;
  return (
    Number.isFinite(updatedAtMs) &&
    ageMs >= -LEGACY_DEPLOY_HANDOFF_FUTURE_SKEW_MS &&
    ageMs < LEGACY_DEPLOY_HANDOFF_FRESH_MS
  );
}

/** Compose renderer-local signals that can precede a fresh canonical build snapshot. */
export function hasDirectLegacyDeployBuildLiveness(args: {
  hasFreshPhaseBProgress: boolean;
  initialKickoffActive: boolean;
  canonicalBuildActive?: boolean;
}): boolean {
  return (
    args.hasFreshPhaseBProgress || args.initialKickoffActive || args.canonicalBuildActive === true
  );
}

/**
 * Positive renderer-local evidence that a rebuild is active while the status
 * poll still carries the response-only legacy-completion projection.
 *
 * Early auto-build stages surface as `drafting-plan`, so their durable active
 * flag must be included explicitly. `build-kickoff-pending` covers the
 * commit/dispatch handoff (including `commit-starting`). A generic `building`
 * presentation state is deliberately not authority here: an ordinary chat
 * turn can produce that state from the shared `__main__` activity row. Build-
 * phase progress and canonical proof arrive through `directBuildLiveness`.
 * Generic activity is deliberately excluded because `currentPhase` survives
 * activity turn rotation and cannot bind a running row to the build turn.
 * Other drafting-plan states are deliberately excluded
 * so unrelated activity cannot recreate the permanent legacy Deploy gate.
 */
export function hasActiveLegacyDeployRebuild(args: {
  runStateKind: WorkspaceRunKind;
  autoBuildStates: readonly ({ status: AutoBuildStatus; updatedAt: string } | null | undefined)[];
  directBuildLiveness: boolean;
  nowMs: number;
}): boolean {
  // The canonical snapshot is evaluated before renderer-local activity when
  // deriving run-state. During a legacy rebuild it can still describe the
  // previous completed run, so direct push/poll/stamp/phase/kickoff liveness must
  // veto Deploy without relying on the snapshot-folded `runStateKind`.
  if (args.directBuildLiveness) return true;

  // The summary and full-state queries refresh independently. Treat either
  // cache's positive liveness as authoritative so a previous terminal record
  // in the other cache cannot briefly reopen Deploy during a rebuild.
  if (args.autoBuildStates.some((state) => isActiveAutoBuild(state))) return true;
  // `commit-starting` is best-effort cleared and can remain persisted after a
  // successful dispatch, so its mere presence cannot be a permanent veto.
  // During the real handoff it is direct positive evidence even when the
  // previous kernel terminal makes run-state look `done`; trust it only inside
  // the same 15-minute liveness window used for stale build progress.
  const commitStartingStates = args.autoBuildStates.filter(
    (state) => state?.status === 'commit-starting',
  );
  if (commitStartingStates.length > 0) {
    return commitStartingStates.some((state) =>
      isFreshLegacyDeployHandoff(state?.updatedAt, args.nowMs),
    );
  }

  return args.runStateKind === 'build-kickoff-pending';
}

// #3494 — plain-language reasons for the git-derived preconditions.
export const DEPLOY_NOT_ON_DEPLOY_BRANCH_REASON =
  "Your latest changes aren't on the main version of your game yet, so there's nothing new to send to the TV.";

export const DEPLOY_UP_TO_DATE_REASON =
  'Your game is already up to date on the TV — there are no new changes to send.';

/**
 * #3494 — the extensible set of reasons the "Save to TV" button can be
 * disabled BEFORE a deploy is attempted. Each maps to plain-language
 * copy in {@link BLOCK_COPY} and, when blocking, to a
 * `deploy:gated:<reason>` debug source. Add a new precondition by
 * adding a member here, its copy below, and a checker in
 * {@link PRECONDITION_CHECKERS} — nothing else changes.
 *
 *   - `not-built` / `build-in-progress` — build readiness (#3132).
 *   - `not-on-deploy-branch` — committed work is on a branch other than
 *     the deploy branch, so a deploy would push nothing new (the
 *     upstream, button-level half of the #3490/CRUCIBLE-206 fix).
 *   - `already-up-to-date` — HEAD has nothing new vs the deployed
 *     remote (#3495 supplies the signal; the member exists now so the
 *     classifier + copy are ready for it).
 */
export type DeployBlockReason =
  | 'not-built'
  | 'build-in-progress'
  | 'not-on-deploy-branch'
  | 'already-up-to-date';

/** The branch the deploy workflow triggers on (mirrors core `DEPLOY_BRANCH`). */
const DEPLOY_BRANCH = 'main';

interface BlockCopy {
  reason: string;
  nextStep?: string;
}

const BLOCK_COPY: Record<DeployBlockReason, BlockCopy> = {
  'not-built': { reason: DEPLOY_NOT_BUILT_REASON },
  'build-in-progress': { reason: DEPLOY_BUILD_IN_PROGRESS_REASON },
  'not-on-deploy-branch': {
    reason: DEPLOY_NOT_ON_DEPLOY_BRANCH_REASON,
    nextStep: 'Save your changes to the main version, then send to the TV again.',
  },
  'already-up-to-date': { reason: DEPLOY_UP_TO_DATE_REASON },
};

/**
 * #3494 — the signals the readiness classifier evaluates. Every field
 * is optional and FAIL-OPEN: a signal that is absent / unknown never
 * blocks (an unknown branch, or an un-fetched up-to-date verdict, must
 * never disable a valid deploy — see #3495's offline rail).
 */
export interface DeployReadinessSignals {
  /** Build-status verdict (#3132). Absence === 'ready'. */
  buildReadiness?: DeployBuildReadiness;
  /** Current checked-out branch (`GameStatus.localGit.branch`). */
  currentBranch?: string | null;
  /**
   * #3495 — true ONLY when we positively know HEAD has nothing new to
   * deploy vs the deployed remote. `undefined` (offline, not-yet-checked,
   * or fetch failed) must leave the button enabled — never a false
   * "up to date" that blocks a valid deploy.
   */
  upToDate?: boolean;
}

/** A blocking precondition: its stable `reason` id plus plain copy. */
export interface DeployPrecondition extends BlockCopy {
  reason: string;
  blockReason: DeployBlockReason;
}

type PreconditionChecker = (s: DeployReadinessSignals) => DeployBlockReason | null;

/**
 * Ordered precondition checkers — the FIRST that fires wins, so order
 * is priority. Build readiness is most fundamental; branch-mismatch
 * outranks already-up-to-date (when your work is on another branch, an
 * "up to date" claim against `main` would be misleading — the
 * actionable truth is the branch).
 */
const PRECONDITION_CHECKERS: readonly PreconditionChecker[] = [
  (s) => {
    const r = s.buildReadiness ?? 'ready';
    return r === 'not-built' || r === 'build-in-progress' ? r : null;
  },
  (s) =>
    typeof s.currentBranch === 'string' &&
    s.currentBranch.length > 0 &&
    s.currentBranch !== DEPLOY_BRANCH
      ? 'not-on-deploy-branch'
      : null,
  (s) => (s.upToDate === true ? 'already-up-to-date' : null),
];

/**
 * Evaluate the deploy preconditions. Returns the first blocking
 * precondition (with plain copy), or `null` when the deploy is ready.
 * Pure + fail-open; the single source of truth for both the button
 * gate ({@link deriveDeployActionState}) and the loud refusal path
 * ({@link refusedDeployStateUpdates}).
 */
export function evaluateDeployReadiness(
  signals: DeployReadinessSignals,
): DeployPrecondition | null {
  for (const check of PRECONDITION_CHECKERS) {
    const blockReason = check(signals);
    if (blockReason) {
      return { blockReason, ...BLOCK_COPY[blockReason] };
    }
  }
  return null;
}

/**
 * Map a persisted build-game interview status to a Deploy readiness
 * verdict (#3132).
 *
 * FAIL-OPEN by design: only statuses we positively KNOW mean
 * "never built" or "build in flight" gate the button. `'built'`,
 * `'built-partial'` (a demoted build stays deployable — its own CI run
 * is the arbiter, per the #2262 product direction and the #3132
 * follow-up), `'abandoned'`, unknown future SKILL terminals, and games
 * with no interview state at all (legacy/imported games) all resolve
 * `'ready'`. A wrong disable is the #2981 permanently-grayed-out class
 * and the #2236 stranded-playable class — worse than a wasted CI run.
 */
export function deriveDeployBuildReadiness(
  status: string | null | undefined,
  activeBuild = false,
): DeployBuildReadiness {
  // The legacy compatibility projection deliberately fails open at rest. A
  // positive renderer-local build signal closes only the overlap window before
  // the next reconciled status poll observes the durable rebuild transition.
  if (status === 'legacy-completion-unverified' && activeBuild) return 'build-in-progress';
  switch (status) {
    case 'in-progress':
    case 'complete':
      return 'not-built';
    case 'dispatched':
    case 'building':
      return 'build-in-progress';
    default:
      return 'ready';
  }
}

/**
 * Resolve which status string feeds {@link deriveDeployBuildReadiness}
 * (Bugbot round 3 on #3132).
 *
 * The RAW persisted interview status is authoritative whenever present
 * — INCLUDING an unrecognised future SKILL terminal, which the
 * readiness switch fails OPEN on. The one exception is the response-only
 * legacy compatibility projection: a newer independent summary poll that
 * positively observes `dispatched` or `building` closes the cross-query
 * rebuild window until the full-state query catches up.
 *
 * Otherwise the status-summary poll is only the fallback for the
 * no-raw-state window (query still loading, or a shell without the
 * get-interview-state channel): the summariser FOLDS unknown persisted
 * statuses to `'in-progress'`, so consulting it while a raw status exists
 * would launder that fold into false knowledge and map a future-terminal
 * game to `'not-built'` — permanently disabling Deploy (#2981 class),
 * exactly what the fail-open contract forbids.
 */
export function resolveDeployReadinessStatus(
  rawInterviewStatus: string | null | undefined,
  summaryStatus: string | null | undefined,
): string | null {
  if (
    rawInterviewStatus === 'legacy-completion-unverified' &&
    (summaryStatus === 'dispatched' || summaryStatus === 'building')
  ) {
    return summaryStatus;
  }
  if (typeof rawInterviewStatus === 'string' && rawInterviewStatus.length > 0) {
    return rawInterviewStatus;
  }
  return summaryStatus ?? null;
}

/**
 * #3132 belt-and-braces — the FULL set of state updates a refused deploy
 * invocation must apply so the refusal is actually VISIBLE.
 *
 * The pre-flight error banner (Workspace.tsx, #2349) renders
 * `deployMessage` only while `deployState === 'error'`, the banner is
 * not dismissed, AND the DeployFailureCard is not showing (mutex so the
 * two never double-render). The card in turn renders whenever
 * `deployFailureCardEnabled` is set with a failure context.
 *
 * Codex P1 (PR #3237 round 1): a stale card armed by a PRIOR CI failure
 * therefore masks a fresh refusal completely — the old card keeps
 * rendering its stale content AND its mutex suppresses the banner, so
 * clicking the card's Retry on a game that has since gone not-ready
 * would surface nothing. That is a recurrence of the exact
 * silent-no-op class #3132 kills. The stale card must YIELD:
 * `deployFailureCardEnabled` flips false as part of the refusal.
 */
export interface RefusedDeployStateUpdates {
  deployState: Extract<DeployState, 'error'>;
  deployMessage: string;
  deployErrorDismissed: false;
  deployFailureCardEnabled: false;
  // #3490 — the refusal's OWN plain copy, so the pre-flight banner can
  // headline it directly. A refusal carries no CrucibleError code, so
  // without this the banner would headline off `deployErrorCode`
  // (null → generic, or a stale prior code) and the real reason would
  // land only in the collapsed "Technical details" (Bugbot #3493, the
  // #3494↔#3490 merge seam). Sourced from BLOCK_COPY — single source of
  // truth, no duplicated strings.
  bannerHeadline: string;
  bannerNextStep?: string;
}

export function refusedDeployStateUpdates(
  // #3494 — widened from the two build-readiness values to any
  // DeployBlockReason so the git-derived preconditions (branch
  // mismatch, already-up-to-date) surface the same loud refusal. The
  // pre-existing 'not-built' / 'build-in-progress' callers still pass.
  reason: DeployBlockReason,
): RefusedDeployStateUpdates {
  const copy = BLOCK_COPY[reason];
  return {
    deployState: 'error',
    deployMessage: copy.reason,
    // Un-dismiss the banner so a previously dismissed error doesn't
    // swallow this fresh refusal.
    deployErrorDismissed: false,
    // Codex P1 — see the interface doc block: the stale failure card
    // yields so the banner mutex releases and the refusal renders.
    deployFailureCardEnabled: false,
    // #3490 — the plain reason IS the headline for a refusal.
    bannerHeadline: copy.reason,
    bannerNextStep: copy.nextStep,
  };
}

// The Deploy-to-TV button is NOT gated on build COMPLETENESS
// (phase / build-order progress / demoted `built-partial` terminals). A
// game is deployable as soon as it is physically deployable — a deploy
// that can't succeed fails CI and surfaces via the `'error'` branch
// (DeployFailureCard + Retry) rather than being pre-blocked here
// (#2262 product direction; the #524-era gate stays removed).
//
// #3132 narrows that by ONE fail-open readiness check: a game we
// positively know has NEVER been built (or is mid-build) gates the
// idle button with an explanation, because deploying it would
// commit + push an unbuilt scaffold. See deriveDeployBuildReadiness.
// The other locks are unchanged: the runtime-capability check
// (`publishBacked`, applied at the Workspace.tsx call site) and the
// in-flight `deployState === 'deploying'` lock below.
export function deriveDeployActionState(input: DeployActionInput): DeployActionState {
  if (input.deployState === 'deploying') {
    return {
      kind: 'deploying',
      label: 'Deploying...',
      tone: 'active',
      badgeClassName: 'bg-yellow-500/20 text-yellow-400',
      deployButtonLabel: 'Deploying...',
      deployButtonTitle: 'Deploy in flight — please wait.',
      deployButtonDisabled: true,
      deployButtonGated: false,
      failureSecondaryCtas: [],
      disabledReason: 'Deploy in flight — please wait.',
      debugSource: 'deploy:deploying',
    };
  }

  if (input.deployState === 'error') {
    const failure =
      input.deployFailureContext?.status === 'failure' ? input.deployFailureContext : undefined;
    const failedJobs = failure?.failedJobs ?? [];
    const hasRunLink = !!(
      failure?.failedRunUrl ??
      failure?.workflowRun?.htmlUrl ??
      failedJobs[0]?.htmlUrl
    );
    const canFixInAgent = input.hasAnthropicKey && failedJobs.length > 0;
    return {
      kind: 'failed',
      label: 'Error',
      tone: 'error',
      badgeClassName: 'bg-red-500/20 text-red-400',
      deployButtonLabel: 'Retry deploy',
      deployButtonTitle: 'Retry deploy against the latest local game state.',
      deployButtonDisabled: false,
      deployButtonGated: false,
      failurePrimaryCta: canFixInAgent ? 'fix-in-agent' : 'retry-deploy',
      failureSecondaryCtas: hasRunLink ? ['view-github'] : [],
      debugSource: canFixInAgent
        ? 'deploy:error:actionable'
        : input.localFailedCheck
          ? 'deploy:error:local-check'
          : input.ciTriggeredThisDeploy
            ? 'deploy:error:ci-no-agent'
            : 'deploy:error:pre-ci',
    };
  }

  if (input.deployState === 'deployed' || input.deployState === 'running') {
    return {
      kind: 'deployed',
      label: input.deployState === 'running' ? 'Running' : 'Deployed',
      tone: 'success',
      badgeClassName: 'bg-green-500/20 text-green-400',
      deployButtonLabel: 'Deploy',
      deployButtonTitle: 'Deploy this game to the TV cluster (commits + pushes + CI).',
      deployButtonDisabled: false,
      deployButtonGated: false,
      failureSecondaryCtas: [],
      debugSource: `deploy:${input.deployState}`,
    };
  }

  if (input.deployState === 'starting') {
    return {
      kind: 'deploying',
      label: 'Starting...',
      tone: 'active',
      badgeClassName: 'bg-blue-500/20 text-blue-400',
      deployButtonLabel: 'Deploy',
      deployButtonTitle: 'Deploy this game to the TV cluster (commits + pushes + CI).',
      deployButtonDisabled: false,
      deployButtonGated: false,
      failureSecondaryCtas: [],
      debugSource: 'deploy:starting',
    };
  }

  // #3132 / #3494 — the readiness gate applies ONLY to the idle/draft
  // branch. The 'deploying' / 'error' / 'deployed' / 'starting' branches
  // above all imply a deploy already happened or is happening, so retry
  // and redeploy stay available regardless of readiness (a rebuild
  // flipping the status back to 'building', or a stale branch signal,
  // must not strand a deployed game's controls). The extensible
  // classifier evaluates every precondition (build readiness +
  // git-derived) and returns the first blocking one, or null when ready.
  const precondition = evaluateDeployReadiness({
    buildReadiness: input.buildReadiness,
    currentBranch: input.currentBranch,
    upToDate: input.upToDate,
  });
  if (precondition) {
    return {
      kind: 'blocked',
      label: 'Draft',
      tone: 'neutral',
      badgeClassName: 'bg-white/10 text-white/50',
      deployButtonLabel: 'Deploy',
      deployButtonTitle: precondition.reason,
      deployButtonDisabled: true,
      deployButtonGated: true,
      failureSecondaryCtas: [],
      disabledReason: precondition.reason,
      debugSource: `deploy:gated:${precondition.blockReason}`,
    };
  }

  return {
    kind: 'idle',
    label: 'Draft',
    tone: 'neutral',
    badgeClassName: 'bg-white/10 text-white/50',
    deployButtonLabel: 'Deploy',
    deployButtonTitle: 'Deploy this game to the TV cluster (commits + pushes + CI).',
    deployButtonDisabled: false,
    deployButtonGated: false,
    failureSecondaryCtas: [],
    debugSource: 'deploy:idle',
  };
}
